<template>
  <div class="container mx-auto px-4">
    <header class="relative text-center py-16 md:py-24 rounded-lg overflow-hidden my-8">
      <div class="absolute inset-0 bg-gradient-to-br from-gray-800 via-gray-900 to-black opacity-80"></div>
      <div class="relative z-10">
        <h1 class="text-4xl md:text-5xl font-extrabold text-white tracking-tight">Kho Tàng Truyện Audio</h1>
        <p class="mt-4 text-lg text-gray-300 max-w-2xl mx-auto">Nghe những câu chuyện hấp dẫn mọi lúc, mọi nơi.</p>
        <div class="mt-8 max-w-xl mx-auto">
          <input
              type="text"
              v-model="currentSearchTerm"
              @input="updateSearchFilter"
              id="search-input"
              placeholder="Tìm kiếm truyện, tác giả..."
              class="w-full p-4 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
          />
        </div>
      </div>
      <router-link to="/history" class="absolute top-4 left-4 p-2 rounded-full text-white hover:bg-white/20 transition-colors z-20">
        <i class="fa-solid fa-history text-xl"></i>
      </router-link>
      <button @click="toggleTheme" id="theme-toggle" class="absolute top-4 right-4 p-2 rounded-full text-white hover:bg-white/20 transition-colors z-20">
        <i class="fa-solid fa-circle-half-stroke text-xl"></i>
      </button>
    </header>

    <div class="flex flex-col md:flex-row gap-8">

      <aside class="md:w-1/3 lg:w-1/4 order-2 md:order-1">
        <div class="sticky top-8 space-y-8">
          <div class="p-6 rounded-lg filter-card">
            <h3 class="text-lg font-semibold mb-4 border-b border-[var(--border-color)] pb-3">Khám phá theo thể loại</h3>
            <div class="grid grid-cols-2 gap-2">
              <router-link
                  :to="{ path: '/', query: { genre: null, search: currentSearchTerm } }"
                  :class="!genre ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'"
                  class="text-xs text-center font-semibold px-3 py-1.5 rounded-md transition-colors truncate"
              >
                Tất cả
              </router-link>
              <router-link
                  v-for="g in genresWithCount"
                  :key="g.name"
                  :to="{ path: '/', query: { genre: g.name, search: currentSearchTerm } }"
                  :class="genre === g.name ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'"
                  class="text-xs text-center font-semibold px-3 py-1.5 rounded-md transition-colors truncate"
              >
                {{ g.name }}
                <span class="ml-1.5 opacity-75">({{ g.count }})</span>
              </router-link>
            </div>
          </div>
        </div>
      </aside>

      <main class="flex-1 order-1 md:order-2">
        <div v-if="loading" id="loading-spinner" class="text-center py-20">
          <div class="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent" role="status"></div>
        </div>

        <div v-if="!loading && paginatedStories.length > 0">
          <div v-if="isFiltered" class="mb-4 p-4 rounded-lg bg-[var(--card-bg-color)] border border-[var(--border-color)] flex items-center justify-between">
            <div>
              <span class="text-sm text-gray-400 block">Đang lọc theo:</span>
              <span v-if="genre" class="inline-block bg-blue-600 text-white text-xs font-semibold mr-2 px-2.5 py-1 rounded mt-1">
                        Thể loại: {{ genre }}
                    </span>
              <span v-if="currentSearchTerm" class="inline-block bg-green-600 text-white text-xs font-semibold mr-2 px-2.5 py-1 rounded mt-1">
                        Từ khóa: "{{ currentSearchTerm }}"
                    </span>
            </div>
            <button @click="clearFilters" class="text-sm text-gray-400 hover:text-white transition-colors">
              <i class="fa-solid fa-times mr-1"></i> Xóa bộ lọc
            </button>
          </div>

          <div class="mb-4 text-sm text-gray-400">
            Hiển thị {{ resultRangeStart }} - {{ resultRangeEnd }} trên tổng số <span class="font-bold text-white">{{ filteredStories.length }}</span> kết quả.
          </div>
          <div id="stories-list" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <StoryCard
                v-for="story in paginatedStories"
                :key="story.slug"
                :story="story"
                :is-active="audioState.story && story.slug === audioState.story.slug"
                @select-story="handleStorySelect"/>
          </div>

          <Pagination
              :total-items="filteredStories.length"
              :items-per-page="storiesPerPage"
              :current-page="currentPage"
              @page-changed="changePage"
          />
        </div>

        <div v-if="!loading && filteredStories.length === 0" class="text-center col-span-full py-20 bg-[var(--card-bg-color)] rounded-lg">
          <p class="text-xl font-semibold">Không tìm thấy truyện phù hợp</p>
          <p class="text-gray-400 mt-2">Hãy thử thay đổi từ khóa tìm kiếm hoặc bộ lọc nhé.</p>
          <button v-if="isFiltered" @click="clearFilters" class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
            Xóa bộ lọc
          </button>
        </div>
      </main>

    </div>
  </div>

  <footer class="text-center py-8 mt-16 text-sm" style="color: var(--text-muted-color);">
    <p>TruyenAudio &copy; 2025</p>
  </footer>
</template>

<script>
import {inject} from 'vue';
import StoryCard from '@/components/StoryCard.vue';
import Pagination from '@/components/AppPagination.vue';

export default {
  name: 'AppHome',
  components: {
    StoryCard,
    Pagination
  },
  props: {
    genre: String,
    searchTerm: String
  },
  setup() {
    // Inject audioState để gọi hàm setTrack
    const audioState = inject('audioState');
    return {audioState};
  },
  data() {
    return {
      allStories: [],
      currentPage: 1,
      storiesPerPage: 9,
      loading: true,
      error: null,
      currentSearchTerm: this.searchTerm || ''
    };
  },
  computed: {
    genresWithCount() {
      const genreCount = {};
      this.allStories.forEach(story => {
        story.genres.forEach(g => {
          genreCount[g] = (genreCount[g] || 0) + 1;
        });
      });
      return Object.keys(genreCount).map(name => ({
        name,
        count: genreCount[name]
      })).sort((a, b) => b.count - a.count);
    },
    filteredStories() {
      let stories = this.allStories;

      if (this.genre) {
        stories = stories.filter(story => story.genres.includes(this.genre));
      }

      if (this.currentSearchTerm) {
        const processedSearchTerm = this.removeDiacritics(this.currentSearchTerm.toLowerCase());
        stories = stories.filter(story => {
          const storyName = this.removeDiacritics(story.name.toLowerCase());
          const authorName = this.removeDiacritics(story.author.toLowerCase());
          return storyName.includes(processedSearchTerm) || authorName.includes(processedSearchTerm);
        });
      }

      return stories;
    },
    paginatedStories() {
      const start = (this.currentPage - 1) * this.storiesPerPage;
      const end = start + this.storiesPerPage;
      return this.filteredStories.slice(start, end);
    },
    isFiltered() {
      return !!this.genre || !!this.currentSearchTerm;
    },
    resultRangeStart() {
      if (this.filteredStories.length === 0) return 0;
      return (this.currentPage - 1) * this.storiesPerPage + 1;
    },
    resultRangeEnd() {
      const end = this.currentPage * this.storiesPerPage;
      return Math.min(end, this.filteredStories.length);
    }
  },
  watch: {
    filteredStories() {
      this.currentPage = 1;
    },
    searchTerm(newVal) {
      this.currentSearchTerm = newVal || '';
    }
  },
  methods: {
    async loadStories() {
      try {
        const response = await fetch('/data/stories.json');
        if (!response.ok) throw new Error('Không thể tải danh sách truyện');
        this.allStories = await response.json();
      } catch (err) {
        this.error = err.message;
        console.error(err);
      } finally {
        this.loading = false;
      }
    },
    // Phương thức mới để xử lý việc chọn truyện
    async handleStorySelect(slug) {

      const response = await fetch(`/data/${slug}.json`);
      if (!response.ok) throw new Error('Không thể tải chi tiết truyện');
      const storyDetails = await response.json();
      storyDetails.slug = slug; // Đảm bảo slug tồn tại trong object

      // Tìm chương đã nghe lần cuối hoặc bắt đầu từ chương 0
      const history = this.loadHistory(slug);
      const chapterIndex = (history && typeof history.chapterIndex === 'number') ? history.chapterIndex : 0;

      // Cập nhật state toàn cục để AudioPlayer hiển thị và phát
      this.audioState.setTrack(storyDetails, chapterIndex);
    },
    loadHistory(slug) {
      const history = localStorage.getItem(`audio-history-${slug}`);
      try {
        return history ? JSON.parse(history) : null;
      } catch (e) {
        return null;
      }
    },
    changePage(page) {
      if (page >= 1 && page <= Math.ceil(this.filteredStories.length / this.storiesPerPage)) {
        this.currentPage = page;
        window.scrollTo({top: 0, behavior: 'smooth'});
      }
    },
    updateSearchFilter() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.$router.push({query: {...this.$route.query, search: this.currentSearchTerm || undefined}});
      }, 300);
    },
    clearFilters() {
      this.currentSearchTerm = '';
      this.$router.push({path: '/'});
    },
    toggleTheme() {
      document.body.classList.toggle('light-mode');
      localStorage.setItem('theme', document.body.classList.contains('light-mode') ? 'light' : 'dark');
    },
    removeDiacritics(str) {
      return str
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/đ/g, 'd')
          .replace(/Đ/g, 'D');
    },
  },
  created() {
    this.loadStories();
    document.title = 'Danh Sách Truyện Audio';
  },
};
</script>

<style>
.filter-card {
  background-color: var(--card-bg-color);
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
}
</style>
